import React, { useMemo, useState, useRef, useEffect, useCallback } from "react";
import { useSearch } from "../search/SearchContext";
import { useAuth } from "../auth/AuthContext";
import { useTranslation } from "react-i18next";
import { useTags, useDocumentTags, batchAddTagToDocuments } from "../../hooks/useTags";
import CompareView from "../detail/CompareView";
import useBatchExport, { EXPORT_ACTIONS, DOWNLOAD_ACTIONS } from "../../hooks/useBatchExport";
import { getApiBase } from "../../utils/env";

const TAG_COLORS = [
  null,      // no color (default gray)
  "#3b82f6", // blue
  "#8b5cf6", // violet
  "#ec4899", // pink
  "#ef4444", // red
  "#f97316", // orange
  "#eab308", // yellow
  "#22c55e", // green
  "#06b6d4", // cyan
  "#6366f1", // indigo
];

const AI_ACTIONS = [
  { key: "summarize", icon: "M4 6h16M4 12h16M4 18h7" },
  { key: "extract", icon: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" },
  { key: "compare", icon: "M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2z" },
  { key: "anomalies", icon: "M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" },
  { key: "report", icon: "M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" },
  { key: "classify", icon: "M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A2 2 0 013 12V7a4 4 0 014-4z" },
  { key: "custom", icon: "M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" },
];

function getCI(obj, keys) {
  if (!obj) return null;
  const map = new Map(Object.keys(obj).map(k => [k.toLowerCase(), k]));
  for (const k of keys) {
    const real = map.get(k.toLowerCase());
    if (real && obj[real] != null) return obj[real];
  }
  return null;
}
function getArchiveNo(doc) {
  return getCI(doc, ["ArchiveNo", "archiveno", "archive", "customerNo", "customerNumber"]);
}
function getDbName(doc) {
  return getCI(doc, ["dbName", "DbName", "database", "Database", "db"]);
}
function getDocLink(doc) {
  return getCI(doc, ["itemId", "ItemId", "link", "Link"]);
}
function getDocName(doc) {
  return doc?.originalName || doc?.fileName || doc?.documentName || doc?.name || "Untitled";
}
function getFileExt(doc) {
  return (getCI(doc, ["fileExtension", "extension", "type"]) || "").toString().toLowerCase();
}
function fmtDate(v) {
  try {
    const dt = typeof v === "string" ? new Date(v) : v;
    if (!isNaN(dt)) return dt.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
    return String(v);
  } catch { return String(v); }
}
function getDocDate(doc) {
  return getCI(doc, ["documentDate", "date", "createdAt", "created"]);
}

function buildDocMetadata(doc, fallbackDb) {
  const meta = {};
  const name = getDocName(doc);
  if (name) meta.fileName = name;
  const ext = getFileExt(doc);
  if (ext) meta.fileExtension = ext;
  const db = getDbName(doc) || fallbackDb;
  if (db) meta.database = db;
  const an = getArchiveNo(doc);
  if (an) meta.archiveNo = an;
  const date = getDocDate(doc);
  if (date) meta.documentDate = typeof date === "string" ? date : new Date(date).toISOString();
  return meta;
}

// Resolve checkedDocs keys (format: "dateLabel-idx") back to row objects
function resolveCheckedRows(checkedDocs, rows) {
  if (!checkedDocs.size || !rows?.length) return [];
  // Build the same dateGroups as ResultsPanel
  const dateGroups = new Map();
  for (const row of rows) {
    const d = getDocDate(row);
    const key = d ? fmtDate(d) : "Unknown Date";
    if (!dateGroups.has(key)) dateGroups.set(key, []);
    dateGroups.get(key).push(row);
  }
  const resolved = [];
  for (const [dateLabel, items] of dateGroups.entries()) {
    items.forEach((row, idx) => {
      if (checkedDocs.has(`${dateLabel}-${idx}`)) resolved.push(row);
    });
  }
  return resolved;
}

// Parse classify AI result markdown table → [{ document, tags: ["tag1","tag2"] }]
function parseClassifyTags(md) {
  if (!md) return [];
  const lines = md.split("\n");
  // Find table header row with DOCUMENT and TAGS columns
  let headerIdx = -1;
  let docCol = -1;
  let tagCol = -1;
  for (let i = 0; i < lines.length - 1; i++) {
    if (lines[i].includes("|") && /^\|?[\s-:|]+\|/.test(lines[i + 1])) {
      const headers = lines[i].replace(/^\||\|$/g, "").split("|").map(c => c.trim().toLowerCase());
      docCol = headers.findIndex(h => h === "document" || h === "doc" || h === "file");
      tagCol = headers.findIndex(h => h === "tags" || h === "tag");
      if (docCol >= 0 && tagCol >= 0) { headerIdx = i; break; }
    }
  }
  if (headerIdx < 0) return [];
  const results = [];
  for (let i = headerIdx + 2; i < lines.length && lines[i].includes("|"); i++) {
    const cells = lines[i].replace(/^\||\|$/g, "").split("|").map(c => c.trim());
    const doc = (cells[docCol] || "").replace(/\*\*/g, "");
    const rawTags = (cells[tagCol] || "").replace(/\*\*/g, "");
    const tags = rawTags.split(/,\s*/).map(t => t.trim()).filter(Boolean);
    if (doc && tags.length) results.push({ document: doc, tags });
  }
  return results;
}

// Simple markdown renderer (handles headings, bold, lists, tables, paragraphs)
function renderMarkdown(md) {
  if (!md) return null;
  const lines = md.split("\n");
  const elements = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Table detection
    if (line.includes("|") && i + 1 < lines.length && /^\|?[\s-:|]+\|/.test(lines[i + 1])) {
      const tableLines = [line];
      i++;
      while (i < lines.length && lines[i].includes("|")) {
        tableLines.push(lines[i]);
        i++;
      }
      const parseRow = (r) => r.replace(/^\||\|$/g, "").split("|").map(c => c.trim());
      const headers = parseRow(tableLines[0]);
      const bodyRows = tableLines.slice(2).map(parseRow);
      elements.push(
        <table key={elements.length} className="ai-result-table">
          <thead><tr>{headers.map((h, j) => <th key={j}>{inlineFormat(h)}</th>)}</tr></thead>
          <tbody>{bodyRows.map((row, ri) => (
            <tr key={ri}>{row.map((cell, ci) => <td key={ci}>{inlineFormat(cell)}</td>)}</tr>
          ))}</tbody>
        </table>
      );
      continue;
    }

    // Headings
    const hMatch = line.match(/^(#{1,4})\s+(.*)/);
    if (hMatch) {
      const level = hMatch[1].length;
      const Tag = `h${Math.min(level + 1, 5)}`;
      elements.push(<Tag key={elements.length} className="ai-result-heading">{inlineFormat(hMatch[2])}</Tag>);
      i++;
      continue;
    }

    // List items
    if (/^\s*[-*]\s+/.test(line)) {
      const listItems = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
        listItems.push(lines[i].replace(/^\s*[-*]\s+/, ""));
        i++;
      }
      elements.push(
        <ul key={elements.length} className="ai-result-list">
          {listItems.map((item, j) => <li key={j}>{inlineFormat(item)}</li>)}
        </ul>
      );
      continue;
    }

    // Numbered list
    if (/^\s*\d+\.\s+/.test(line)) {
      const listItems = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        listItems.push(lines[i].replace(/^\s*\d+\.\s+/, ""));
        i++;
      }
      elements.push(
        <ol key={elements.length} className="ai-result-list">
          {listItems.map((item, j) => <li key={j}>{inlineFormat(item)}</li>)}
        </ol>
      );
      continue;
    }

    // Horizontal rule
    if (/^---+$/.test(line.trim())) {
      elements.push(<hr key={elements.length} className="ai-result-hr" />);
      i++;
      continue;
    }

    // Empty line
    if (!line.trim()) {
      i++;
      continue;
    }

    // Paragraph
    elements.push(<p key={elements.length} className="ai-result-p">{inlineFormat(line)}</p>);
    i++;
  }

  return elements;
}

function inlineFormat(text) {
  if (!text) return text;
  // Bold
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
}

export default function ResultsFooterBar() {
  const { t } = useTranslation();
  const { token } = useAuth();
  const { selectedGroup, rows, selectedDoc, dbs, rawResults, checkedDocs, pinnedForCompare, unpinForCompare, clearCompare } = useSearch();
  const { allTags, createTag, updateTag, deleteTag } = useTags();

  const CORE_API_BASE = useMemo(() => getApiBase(), []);

  const docDb = selectedDoc
    ? (getDbName(selectedDoc)
      || (rawResults?.responses?.[0]?.database || rawResults?.responses?.[0]?.dbName)
      || (dbs?.[0]?.dbName)
      || null)
    : null;
  const docAn = selectedDoc ? getArchiveNo(selectedDoc) : null;
  const { tags: docTags, addTag, removeTag } = useDocumentTags(docDb, docAn);

  const [tagOpen, setTagOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState(null);
  const [colorEditId, setColorEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const popoverRef = useRef(null);
  const btnRef = useRef(null);

  // Compare view state
  const [compareViewOpen, setCompareViewOpen] = useState(false);

  // AI dropdown state
  const [aiMenuOpen, setAiMenuOpen] = useState(false);
  const [aiCustomPrompt, setAiCustomPrompt] = useState("");
  const [aiShowCustom, setAiShowCustom] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null); // { result, model, action } or null
  const [aiError, setAiError] = useState("");
  const [aiApplying, setAiApplying] = useState(false);
  const [aiApplyDone, setAiApplyDone] = useState(false);
  const aiMenuRef = useRef(null);
  const aiBtnRef = useRef(null);

  // Download dropdown state
  const [downloadMenuOpen, setDownloadMenuOpen] = useState(false);
  const downloadMenuRef = useRef(null);
  const downloadBtnRef = useRef(null);

  // Export dropdown state
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const exportMenuRef = useRef(null);
  const exportBtnRef = useRef(null);

  const scope = useMemo(() => {
    return selectedGroup?.items && Array.isArray(selectedGroup.items)
      ? selectedGroup.items
      : rows || [];
  }, [selectedGroup, rows]);
  const label = selectedGroup?.name
    ? `${selectedGroup.name} (${scope.length})`
    : t("archive.results", { count: scope.length });
  const hasResults = scope.length > 0;
  const hasSelectedDoc = !!(docDb && docAn);

  // What the batch actions can actually act on.
  //
  // `hasResults` describes a SEARCH RESULT SET, and gating Run AI on it left the button permanently
  // disabled for a document opened from anywhere else — a personal folder, a recent-documents link —
  // even though `getAiDocuments()` below has always fallen back to the open document, exactly as
  // Download and Export do. The action worked; only the gate said it could not.
  const hasActionable = hasResults || !!selectedDoc;

  const fallbackDb = useMemo(() => {
    return (rawResults?.responses?.[0]?.database || rawResults?.responses?.[0]?.dbName)
      || dbs?.[0]?.dbName || null;
  }, [rawResults, dbs]);

  const { exportLoading, exportError, exportPhase, runExport, clearExportError } = useBatchExport({ token, apiBase: CORE_API_BASE, fallbackDb });

  // Close tag popover on outside click
  useEffect(() => {
    if (!tagOpen) return;
    const handler = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target) &&
          btnRef.current && !btnRef.current.contains(e.target)) {
        setTagOpen(false);
        setCreating(false);
        setNewName("");
        setNewColor(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [tagOpen]);

  // Close AI menu on outside click
  useEffect(() => {
    if (!aiMenuOpen) return;
    const handler = (e) => {
      if (aiMenuRef.current && !aiMenuRef.current.contains(e.target) &&
          aiBtnRef.current && !aiBtnRef.current.contains(e.target)) {
        setAiMenuOpen(false);
        setAiShowCustom(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [aiMenuOpen]);

  // Close download menu on outside click
  useEffect(() => {
    if (!downloadMenuOpen) return;
    const handler = (e) => {
      if (downloadMenuRef.current && !downloadMenuRef.current.contains(e.target) &&
          downloadBtnRef.current && !downloadBtnRef.current.contains(e.target)) {
        setDownloadMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [downloadMenuOpen]);

  // Close export menu on outside click
  useEffect(() => {
    if (!exportMenuOpen) return;
    const handler = (e) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target) &&
          exportBtnRef.current && !exportBtnRef.current.contains(e.target)) {
        setExportMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [exportMenuOpen]);

  // Close popover when selected doc changes
  useEffect(() => {
    setTagOpen(false);
    setCreating(false);
    setNewName("");
    setNewColor(null);
  }, [docDb, docAn]);

  const docTagIds = useMemo(() => new Set(docTags.map((dt) => dt.tagId)), [docTags]);
  const availableTags = useMemo(() => allTags.filter((t) => !docTagIds.has(t.id)), [allTags, docTagIds]);

  // Determine which documents to send to AI
  const getAiDocuments = useCallback(() => {
    const checked = resolveCheckedRows(checkedDocs, rows);
    const docs = checked.length > 0 ? checked : (selectedDoc ? [selectedDoc] : (rows || []).slice(0, 10));
    return docs.map((doc) => ({
      link: getDocLink(doc) || "",
      metadata: buildDocMetadata(doc, fallbackDb),
    }));
  }, [checkedDocs, rows, selectedDoc, fallbackDb]);

  const aiDocCount = useMemo(() => {
    const checked = resolveCheckedRows(checkedDocs, rows);
    if (checked.length > 0) return checked.length;
    if (selectedDoc) return 1;
    return Math.min((rows || []).length, 10);
  }, [checkedDocs, rows, selectedDoc]);

  const handleAiAction = useCallback(async (action, customPrompt) => {
    setAiMenuOpen(false);
    setAiShowCustom(false);
    setAiLoading(true);
    setAiError("");
    setAiResult(null);
    setAiApplyDone(false);

    const documents = getAiDocuments();
    try {
      const resp = await fetch(`${CORE_API_BASE}/api/AiAssist/documents`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ action, customPrompt: customPrompt || null, documents }),
      });

      if (!resp.ok) {
        const errBody = await resp.json().catch(() => ({}));
        throw new Error(errBody.message || `HTTP ${resp.status}`);
      }

      const data = await resp.json();
      setAiResult({ result: data.result, model: data.model, action });
    } catch (err) {
      setAiError(err.message || t("ai.errorGeneric"));
    } finally {
      setAiLoading(false);
    }
  }, [getAiDocuments, token, CORE_API_BASE, t]);

  // Apply AI-suggested classify tags to documents
  const handleApplyClassifyTags = useCallback(async () => {
    if (!aiResult?.result || aiApplying) return;
    const parsed = parseClassifyTags(aiResult.result);
    if (!parsed.length) return;

    setAiApplying(true);
    try {
      // Collect all unique tag names
      const allTagNames = [...new Set(parsed.flatMap(p => p.tags))];

      // Create tags that don't exist yet (match case-insensitively)
      const tagNameToId = new Map();
      for (const existing of allTags) {
        tagNameToId.set(existing.name.toLowerCase(), existing.id);
      }
      for (const name of allTagNames) {
        if (!tagNameToId.has(name.toLowerCase())) {
          const res = await createTag(name, "", null);
          if (res.success && res.tag) {
            tagNameToId.set(name.toLowerCase(), res.tag.id);
          }
        }
      }

      // Match by row index — the AI result rows correspond 1:1 with the docs we sent
      const docsInScope = getAiDocuments();
      for (let idx = 0; idx < parsed.length; idx++) {
        const entry = parsed[idx];
        // Use index-based match (AI returns rows in the same order we sent them)
        const doc = docsInScope[idx];
        if (!doc) continue;

        const dbName = doc.metadata?.database || fallbackDb || "";
        const archiveNo = doc.metadata?.archiveNo || "";
        if (!dbName || !archiveNo) continue;

        const documents = [{ dbName, archiveNo }];
        for (const tagName of entry.tags) {
          const tagId = tagNameToId.get(tagName.toLowerCase());
          if (tagId) {
            await batchAddTagToDocuments(token, tagId, documents);
          }
        }
      }

      setAiApplyDone(true);
      window.dispatchEvent(new CustomEvent("tags-updated"));
    } catch (err) {
      console.error("Failed to apply classify tags:", err);
    } finally {
      setAiApplying(false);
    }
  }, [aiResult, aiApplying, allTags, createTag, getAiDocuments, fallbackDb, token]);

  const handleApplyTag = useCallback(async (tagId) => {
    if (busy) return;
    setBusy(true);
    const result = await addTag(tagId);
    setBusy(false);
    if (result.success) {
      window.dispatchEvent(new CustomEvent("tags-updated"));
    }
  }, [addTag, busy]);

  const handleRemoveTag = useCallback(async (tagId) => {
    if (busy) return;
    setBusy(true);
    const result = await removeTag(tagId);
    setBusy(false);
    if (result.success) {
      window.dispatchEvent(new CustomEvent("tags-updated"));
    }
  }, [removeTag, busy]);

  const handleCreateAndApply = useCallback(async () => {
    const name = newName.trim();
    if (!name || busy) return;
    setBusy(true);
    const result = await createTag(name, "", newColor);
    if (result.success && result.tag) {
      await addTag(result.tag.id);
      setCreating(false);
      setNewName("");
      setNewColor(null);
      window.dispatchEvent(new CustomEvent("tags-updated"));
    }
    setBusy(false);
  }, [newName, newColor, busy, createTag, addTag]);

  const handleDeleteTag = useCallback((tagId, tagName) => {
    if (busy) return;
    setDeleteConfirm({ id: tagId, name: tagName });
  }, [busy]);

  const confirmDeleteTag = useCallback(async () => {
    if (!deleteConfirm || busy) return;
    setBusy(true);
    await deleteTag(deleteConfirm.id);
    setBusy(false);
    setDeleteConfirm(null);
    window.dispatchEvent(new CustomEvent("tags-updated"));
  }, [deleteConfirm, deleteTag, busy]);

  const handleColorChange = useCallback(async (tagId, color) => {
    if (busy) return;
    setBusy(true);
    await updateTag(tagId, { color: color || "" });
    setBusy(false);
    window.dispatchEvent(new CustomEvent("tags-updated"));
  }, [updateTag, busy]);

  // Export: resolve selected documents
  const getExportDocuments = useCallback(() => {
    const checked = resolveCheckedRows(checkedDocs, rows);
    return checked.length > 0 ? checked : (selectedDoc ? [selectedDoc] : []);
  }, [checkedDocs, rows, selectedDoc]);

  const exportDocCount = useMemo(() => {
    const checked = resolveCheckedRows(checkedDocs, rows);
    if (checked.length > 0) return checked.length;
    if (selectedDoc) return 1;
    return 0;
  }, [checkedDocs, rows, selectedDoc]);

  const hasExportSelection = exportDocCount > 0;

  const handleExportAction = useCallback((actionId) => {
    setExportMenuOpen(false);
    clearExportError();
    const docs = getExportDocuments();
    if (!docs.length) return;
    runExport(actionId, docs);
  }, [getExportDocuments, runExport, clearExportError]);

  return (
    <>
    <div className="results-footer">
      <div className="results-footer-left">{label}</div>
      <div className="results-footer-batch">
        <span className="batch-bar-label">{t("batch.label")}</span>
        {/* Download button with dropdown */}
        <div style={{ position: "relative" }}>
          <button
            ref={downloadBtnRef}
            className={`batch-bar-btn${downloadMenuOpen ? " active" : ""}${(exportLoading === "download_zip" || exportLoading === "download_pdf_merge") ? " batch-bar-btn--busy" : ""}`}
            disabled={!hasExportSelection && !(exportLoading === "download_zip" || exportLoading === "download_pdf_merge")}
            onClick={() => { if (!(exportLoading === "download_zip" || exportLoading === "download_pdf_merge")) setDownloadMenuOpen(v => !v); }}
            title={!hasExportSelection && !(exportLoading === "download_zip" || exportLoading === "download_pdf_merge") ? t("export.selectHint") : ""}
          >
            {(exportLoading === "download_zip" || exportLoading === "download_pdf_merge") ? (
              <div className="ai-loading-spinner ai-loading-spinner--small" />
            ) : (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            )}
            {(exportLoading === "download_zip" || exportLoading === "download_pdf_merge") && exportPhase
              ? t(`export.phase.${exportPhase}`)
              : t("batch.download")}
            {!(exportLoading === "download_zip" || exportLoading === "download_pdf_merge") && (
              <svg width="8" height="8" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 1 }}>
                <polyline points="2,3.5 5,6.5 8,3.5" />
              </svg>
            )}
          </button>

          {downloadMenuOpen && (() => {
            const rect = downloadBtnRef.current?.getBoundingClientRect();
            const style = rect
              ? { bottom: window.innerHeight - rect.top + 6, left: rect.left }
              : { bottom: 40, left: 0 };
            return (
              <div className="export-action-menu" ref={downloadMenuRef} style={style}>
                <div className="export-action-menu-header">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                  <span>{t("batch.download")} ({exportDocCount} {exportDocCount === 1 ? t("export.document") : t("export.documents")})</span>
                </div>
                {DOWNLOAD_ACTIONS.map((action) => (
                  <button
                    key={action.id}
                    className="export-action-menu-item"
                    onClick={() => { setDownloadMenuOpen(false); clearExportError(); const docs = getExportDocuments(); if (docs.length) runExport(action.id, docs); }}
                    disabled={!!exportLoading}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d={action.icon}/></svg>
                    <span>{t(`export.action.${action.id}`)}</span>
                  </button>
                ))}
                {exportError && (
                  <div className="export-error-hint">
                    {t("export.failed")}
                  </div>
                )}
              </div>
            );
          })()}
        </div>
        {/* Export button with dropdown */}
        <div style={{ position: "relative" }}>
          <button
            ref={exportBtnRef}
            className={`batch-bar-btn${exportMenuOpen ? " active" : ""}${exportLoading ? " batch-bar-btn--busy" : ""}`}
            disabled={!hasExportSelection && !exportLoading}
            onClick={() => { if (!exportLoading) setExportMenuOpen(v => !v); }}
            title={!hasExportSelection && !exportLoading ? t("export.selectHint") : exportPhase ? t(`export.phase.${exportPhase}`) : ""}
          >
            {exportLoading ? (
              <div className="ai-loading-spinner ai-loading-spinner--small" />
            ) : (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>
            )}
            {exportLoading && exportPhase ? t(`export.phase.${exportPhase}`) : t("batch.export")}
            {!exportLoading && (
              <svg width="8" height="8" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 1 }}>
                <polyline points="2,3.5 5,6.5 8,3.5" />
              </svg>
            )}
          </button>

          {exportMenuOpen && (() => {
            const rect = exportBtnRef.current?.getBoundingClientRect();
            const style = rect
              ? { bottom: window.innerHeight - rect.top + 6, left: rect.left }
              : { bottom: 40, left: 0 };
            return (
              <div className="export-action-menu" ref={exportMenuRef} style={style}>
                <div className="export-action-menu-header">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>
                  <span>{t("export.menuTitle")} ({exportDocCount} {exportDocCount === 1 ? t("export.document") : t("export.documents")})</span>
                </div>
                {EXPORT_ACTIONS.map((action) => (
                  <button
                    key={action.id}
                    className="export-action-menu-item"
                    onClick={() => handleExportAction(action.id)}
                    disabled={!!exportLoading}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d={action.icon}/></svg>
                    <span>{t(`export.action.${action.id}`)}</span>
                  </button>
                ))}
                {exportError && (
                  <div className="export-error-hint">
                    {exportError === "not_implemented" ? t("export.notImplemented") : t("export.failed")}
                  </div>
                )}
              </div>
            );
          })()}
        </div>
        <button className="batch-bar-btn" disabled={!hasResults}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
          {t("batch.send")}
        </button>

        {/* Run AI button with dropdown */}
        <div style={{ position: "relative" }}>
          <button
            ref={aiBtnRef}
            className={`batch-bar-btn batch-bar-btn--ai${aiMenuOpen ? " active" : ""}`}
            disabled={!hasActionable}
            onClick={() => { setAiMenuOpen(v => !v); setAiShowCustom(false); }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/></svg>
            {t("batch.runAi")}
            <svg width="8" height="8" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 1 }}>
              <polyline points="2,3.5 5,6.5 8,3.5" />
            </svg>
          </button>

          {aiMenuOpen && (() => {
            const rect = aiBtnRef.current?.getBoundingClientRect();
            const style = rect
              ? { bottom: window.innerHeight - rect.top + 6, left: rect.left }
              : { bottom: 40, left: 0 };
            return (
              <div className="ai-action-menu" ref={aiMenuRef} style={style}>
                <div className="ai-action-menu-header">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/></svg>
                  <span>{t("ai.menuTitle")} ({aiDocCount} {aiDocCount === 1 ? t("ai.document") : t("ai.documents")})</span>
                </div>
                {AI_ACTIONS.map((a) => (
                  a.key === "custom" ? (
                    <div key={a.key}>
                      <button
                        className="ai-action-menu-item"
                        onClick={() => setAiShowCustom(v => !v)}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d={a.icon}/></svg>
                        <span>{t(`ai.action.${a.key}`)}</span>
                        <svg width="8" height="8" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ marginLeft: "auto" }}>
                          <polyline points={aiShowCustom ? "2,6.5 5,3.5 8,6.5" : "2,3.5 5,6.5 8,3.5"} />
                        </svg>
                      </button>
                      {aiShowCustom && (
                        <div className="ai-custom-prompt-box">
                          <textarea
                            className="ai-custom-textarea"
                            value={aiCustomPrompt}
                            onChange={(e) => setAiCustomPrompt(e.target.value)}
                            placeholder={t("ai.customPlaceholder")}
                            rows={3}
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && aiCustomPrompt.trim()) {
                                handleAiAction("custom", aiCustomPrompt.trim());
                              }
                            }}
                          />
                          <button
                            className="ai-custom-submit"
                            disabled={!aiCustomPrompt.trim()}
                            onClick={() => handleAiAction("custom", aiCustomPrompt.trim())}
                          >
                            {t("ai.run")}
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <button
                      key={a.key}
                      className="ai-action-menu-item"
                      onClick={() => handleAiAction(a.key)}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d={a.icon}/></svg>
                      <span>{t(`ai.action.${a.key}`)}</span>
                    </button>
                  )
                ))}
              </div>
            );
          })()}
        </div>

        {/* Compare tray */}
        {pinnedForCompare.length > 0 && (
          <>
            <div className="batch-bar-sep" />
            <div className="compare-tray">
              <div className="compare-tray-trigger">
                <span className="compare-tray-badge">{pinnedForCompare.length}</span>
                {/* Hover popover showing pinned docs */}
                <div className="compare-tray-popover">
                  <div className="compare-tray-popover-title">{t("compare.pinnedCount", { count: pinnedForCompare.length })}</div>
                  {pinnedForCompare.map((doc, i) => {
                    const ext = getFileExt(doc) || "doc";
                    const name = getDocName(doc);
                    return (
                      <div key={i} className="compare-tray-popover-item">
                        <span className={`rp-file-badge ${ext}`}>.{ext}</span>
                        <span className="compare-tray-popover-name">{name}</span>
                        <button
                          className="compare-tray-popover-x"
                          onClick={(e) => { e.stopPropagation(); unpinForCompare(doc); }}
                        >&times;</button>
                      </div>
                    );
                  })}
                </div>
              </div>
              <button
                className="batch-bar-btn batch-bar-btn--compare"
                onClick={() => setCompareViewOpen(true)}
                disabled={pinnedForCompare.length < 2}
                title={pinnedForCompare.length < 2 ? t("compare.needTwo") : t("compare.compareNow")}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="3" width="8" height="18" rx="1"/><rect x="14" y="3" width="8" height="18" rx="1"/>
                </svg>
                {t("compare.compareNow")}
              </button>
              <button className="compare-tray-clear" onClick={clearCompare} title={t("compare.clearAll")}>
                &times;
              </button>
            </div>
          </>
        )}

        <div className="batch-bar-sep" />
        <div style={{ position: "relative" }}>
          <button
            ref={btnRef}
            className={`batch-bar-btn${tagOpen ? " active" : ""}`}
            disabled={!hasSelectedDoc}
            onClick={() => { setTagOpen((v) => !v); setCreating(false); setNewName(""); setNewColor(null); }}
            title={!hasSelectedDoc ? t("tags.selectDocFirst", { defaultValue: "Select a document first" }) : ""}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>
            {t("batch.tag")}
          </button>

          {tagOpen && hasSelectedDoc && (() => {
            const rect = btnRef.current?.getBoundingClientRect();
            const style = rect
              ? { bottom: window.innerHeight - rect.top + 6, right: window.innerWidth - rect.right }
              : { bottom: 40, right: 16 };
            return (
            <div className="tag-popover" ref={popoverRef} style={style}>
              <div className="tag-popover-header">
                <span>{t("tags.documentTags")}</span>
              </div>

              {/* Current tags on document */}
              {docTags.length > 0 && (
                <div className="tag-popover-current">
                  {docTags.map((dt) => {
                    const tagObj = allTags.find((t) => t.id === dt.tagId);
                    const color = tagObj?.color || null;
                    const isEditing = colorEditId === dt.tagId;
                    return (
                      <div key={dt.tagId} style={{ display: "inline-flex", flexDirection: "column", gap: 2 }}>
                        <span
                          className="tag-popover-chip"
                          style={color ? { background: color + "18", color, borderColor: color + "40" } : undefined}
                        >
                          <button
                            className="tag-popover-chip-dot"
                            style={{ background: color || "#9ca3af" }}
                            onClick={() => setColorEditId(isEditing ? null : dt.tagId)}
                            title="Change color"
                          />
                          {dt.tagName}
                          <button
                            className="tag-popover-chip-x"
                            onClick={() => handleRemoveTag(dt.tagId)}
                            disabled={busy}
                            title={t("tags.removeTag")}
                          >
                            &times;
                          </button>
                        </span>
                        {isEditing && (
                          <div className="tag-color-row">
                            {TAG_COLORS.map((c, i) => (
                              <button
                                key={i}
                                className={`tag-color-swatch${!c ? " tag-color-swatch--none" : ""}${color === c ? " active" : ""}`}
                                style={c ? { background: c } : undefined}
                                onClick={() => { handleColorChange(dt.tagId, c); setColorEditId(null); }}
                                disabled={busy}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Available tags to add */}
              <div className="tag-popover-list">
                {availableTags.length === 0 && docTags.length === 0 && !creating && (
                  <div className="tag-popover-empty">{t("tags.noAvailableTags")}</div>
                )}
                {availableTags.map((tag) => (
                  <div key={tag.id} className="tag-popover-item-row">
                    <button
                      className="tag-popover-item"
                      onClick={() => handleApplyTag(tag.id)}
                      disabled={busy}
                    >
                      <span className="tag-dot" style={{ background: tag.color || "#9ca3af" }} />
                      <span>{tag.name}</span>
                    </button>
                    <button
                      className="tag-popover-delete-btn"
                      onClick={(e) => { e.stopPropagation(); handleDeleteTag(tag.id, tag.name); }}
                      disabled={busy}
                      title={t("tags.deleteTag", { defaultValue: "Delete tag" })}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                    </button>
                  </div>
                ))}
              </div>

              {/* Create new tag with color */}
              <div className="tag-popover-footer">
                {!creating ? (
                  <button
                    className="tag-popover-create-btn"
                    onClick={() => setCreating(true)}
                  >
                    + {t("tags.createNewTag")}
                  </button>
                ) : (
                  <div>
                    <div style={{ display: "flex", gap: 4 }}>
                      <input
                        type="text"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        placeholder={t("tags.tagNamePlaceholder")}
                        maxLength={100}
                        style={{
                          flex: 1, fontSize: 11, padding: "4px 8px", border: "1px solid #dde1ea",
                          borderRadius: 6, outline: "none",
                        }}
                        onKeyDown={(e) => { if (e.key === "Enter") handleCreateAndApply(); if (e.key === "Escape") { setCreating(false); setNewName(""); setNewColor(null); } }}
                        autoFocus
                        disabled={busy}
                      />
                      <button
                        onClick={handleCreateAndApply}
                        disabled={!newName.trim() || busy}
                        style={{
                          fontSize: 11, fontWeight: 600, padding: "4px 10px", borderRadius: 6,
                          border: "none", background: "#3b5bdb", color: "#fff", cursor: "pointer",
                          opacity: !newName.trim() || busy ? 0.5 : 1,
                        }}
                      >
                        {t("tags.addTag")}
                      </button>
                    </div>
                    <div className="tag-color-row">
                      {TAG_COLORS.map((c, i) => (
                        <button
                          key={i}
                          className={`tag-color-swatch${!c ? " tag-color-swatch--none" : ""}${newColor === c ? " active" : ""}`}
                          style={c ? { background: c } : undefined}
                          onClick={() => setNewColor(c)}
                          title={c || "No color"}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            );
          })()}
        </div>
      </div>
    </div>

    {/* AI Results Drawer */}
    {(aiLoading || aiResult || aiError) && (
      <div className="ai-result-overlay" onClick={() => { if (!aiLoading) { setAiResult(null); setAiError(""); } }}>
        <div className="ai-result-drawer" onClick={(e) => e.stopPropagation()}>
          <div className="ai-result-drawer-header">
            <div className="ai-result-drawer-title">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/></svg>
              <span>
                {aiLoading
                  ? t("ai.analyzing")
                  : aiResult
                    ? t(`ai.action.${aiResult.action}`)
                    : t("ai.error")}
              </span>
              {aiResult?.model && <span className="ai-result-model">{aiResult.model}</span>}
            </div>
            <button
              className="ai-result-drawer-close"
              onClick={() => { setAiResult(null); setAiError(""); setAiLoading(false); }}
            >
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                <line x1="2" y1="2" x2="8" y2="8"/><line x1="8" y1="2" x2="2" y2="8"/>
              </svg>
            </button>
          </div>
          <div className="ai-result-drawer-body">
            {aiLoading && (
              <div className="ai-result-loading">
                <div className="ai-loading-spinner" />
                <span>{t("ai.analyzing")}</span>
              </div>
            )}
            {aiError && (
              <div className="ai-result-error">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
                <span>{aiError}</span>
              </div>
            )}
            {aiResult && (
              <div className="ai-result-content">
                {renderMarkdown(aiResult.result)}
              </div>
            )}
            {aiResult?.action === "classify" && !aiApplyDone && (
              <div className="ai-result-drawer-footer">
                <button
                  className="ai-apply-tags-btn"
                  disabled={aiApplying}
                  onClick={handleApplyClassifyTags}
                >
                  {aiApplying ? (
                    <>
                      <div className="ai-loading-spinner ai-loading-spinner--small" />
                      {t("ai.applyingTags")}
                    </>
                  ) : (
                    <>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A2 2 0 013 12V7a4 4 0 014-4z"/></svg>
                      {t("ai.applyTags")}
                    </>
                  )}
                </button>
              </div>
            )}
            {aiResult?.action === "classify" && aiApplyDone && (
              <div className="ai-result-drawer-footer">
                <span className="ai-apply-done">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  {t("ai.tagsApplied")}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    )}

    {compareViewOpen && pinnedForCompare.length >= 2 && (
      <CompareView
        documents={pinnedForCompare}
        onClose={() => setCompareViewOpen(false)}
        onRemove={(doc) => {
          unpinForCompare(doc);
          if (pinnedForCompare.length <= 2) setCompareViewOpen(false);
        }}
        token={token}
        apiBase={CORE_API_BASE}
      />
    )}

    {deleteConfirm && (
      <div className="dbModalOverlay" onClick={() => setDeleteConfirm(null)}>
        <div className="dbModalCard dbModalConfirmCard" onClick={(e) => e.stopPropagation()}>
          <button
            className="drawer-close-btn drawer-close-btn-abs"
            onClick={() => setDeleteConfirm(null)}
            aria-label="Close"
            title="Close"
          >
            <svg width="8" height="8" viewBox="0 0 8 8" fill="none" aria-hidden="true">
              <line x1="1" y1="1" x2="7" y2="7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
              <line x1="7" y1="1" x2="1" y2="7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
            </svg>
          </button>

          <div className="dbModalConfirmBadge dbModalConfirmBadge--danger">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
          </div>

          <div className="dbModalTitle">
            {t("tags.deleteTagTitle", { defaultValue: "Delete tag" })}
          </div>

          <div className="dbModalDbName">{deleteConfirm.name}</div>

          <div className="dbModalConfirmBanner dbModalConfirmBanner--danger">
            <p>{t("tags.deleteConfirmDesc", { defaultValue: "This tag will be permanently removed from all documents. This cannot be undone." })}</p>
          </div>

          <div className="dbModalConfirmActions">
            <button type="button" className="dbModalCancel" onClick={() => setDeleteConfirm(null)}>
              {t("admin.cancel", { defaultValue: "Cancel" })}
            </button>
            <button
              type="button"
              className="dbModalConfirmBtn dbModalConfirmBtn--danger"
              disabled={busy}
              onClick={confirmDeleteTag}
            >
              {t("tags.deleteTag", { defaultValue: "Delete tag" })}
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
